import { APP_NAME } from '@clanfire/shared';
import { Users } from '@/components/ui/icons';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { InvitePreviewDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { inviteApi } from '@/api/endpoints';
import { Wordmark } from '@/components/Brand';
import { Avatar, ServerIcon } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/stores/ui';

export function InvitePage() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<InvitePreviewDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    let cancelled = false;
    inviteApi
      .preview(code)
      .then((p) => !cancelled && setPreview(p))
      .catch((err) => !cancelled && setError(errorMessage(err)));
    return () => {
      cancelled = true;
    };
  }, [code]);

  const accept = async () => {
    if (!preview) return;
    if (preview.alreadyMember) return navigate(`/app/${preview.server.id}`);
    setJoining(true);
    try {
      const server = await inviteApi.accept(code);
      toast.success(`Welcome to ${server.name}!`);
      navigate(`/app/${server.id}`);
    } catch (err) {
      setError(errorMessage(err));
      setJoining(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-sidebar px-4">
      <Wordmark />
      <div className="w-full max-w-[440px] animate-slide-up rounded-2xl border border-line bg-surface p-8 text-center shadow-pop">
        {!preview && !error && (
          <div className="flex justify-center py-8 text-fg-muted">
            <Spinner />
          </div>
        )}
        {error && !preview && (
          <>
            <h1 className="text-xl font-bold">Invite invalid</h1>
            <p className="mt-2 text-fg-muted">{error}</p>
            <Link to="/app/@me" className="mt-6 inline-block">
              <Button>Continue to {APP_NAME}</Button>
            </Link>
          </>
        )}
        {preview && (
          <>
            <div className="flex justify-center">
              <ServerIcon server={preview.server} size={80} />
            </div>
            {preview.inviter && (
              <p className="mt-5 flex items-center justify-center gap-2 text-sm text-fg-muted">
                <Avatar user={preview.inviter} size={20} /> {preview.inviter.displayName} invited you to join
              </p>
            )}
            <h1 className="mt-1 text-2xl font-extrabold tracking-tight">{preview.server.name}</h1>
            {preview.server.description && <p className="mt-2 text-sm text-fg-muted">{preview.server.description}</p>}
            <div className="mt-4 flex items-center justify-center gap-4 text-sm text-fg-muted">
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-online" /> {preview.onlineCount} online
              </span>
              <span className="flex items-center gap-1.5">
                <Users size={14} /> {preview.memberCount} {preview.memberCount === 1 ? 'member' : 'members'}
              </span>
            </div>
            {preview.paused && !preview.alreadyMember && !error && (
              <p className="mt-4 text-sm font-medium text-warning">This server isn&apos;t letting new people in right now. Please try again later.</p>
            )}
            {error && <p className="mt-4 text-sm font-medium text-danger">{error}</p>}
            <Button className="mt-6" size="lg" block loading={joining} disabled={preview.paused && !preview.alreadyMember} onClick={accept}>
              {preview.alreadyMember ? 'Open server' : 'Accept invite'}
            </Button>
            <Link to="/app/@me" className="mt-3 inline-block text-sm text-fg-muted hover:text-fg-soft hover:underline">
              No thanks
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
